<template>
  <asd20-modal
    v-if="editedFile"
    :open="open"
    title="File Details"
    icon="files"
    dismissable
    class="asd20-file-modal"
    @dismiss="$emit('dismiss')"
  >
    <header class="asd20-file-modal__header"></header>
    <Asd20Viewport class="asd20-file-modal__details" scrollable>
      <Asd20TextInput
        v-if="editedFile.id"
        v-model="editedFile.name"
        label="Name"
      />
      <Asd20TextInput v-model="editedFile.description" label="Description" />

      <Asd20MultiselectInput
        :value="selectedOrganizations"
        :items="organizations"
        item-label="title"
        item-value="id"
        label="Organizations"
        required
        @input="editedFile.organizationIds = $event.map(e => e.id)"
      />

      <Asd20MultiselectInput
        :value="selectedCategoryOptions"
        :items="categoryOptions"
        label="Categories"
        required
        @input="editedFile.categories = $event.map(e => e.id)"
      />
      <Asd20MultiselectInput
        :value="selectedOwnerOptions"
        :items="ownerOptions"
        label="Owners"
        required
        @input="editedFile.owners = $event.map(e => e.id)"
      />

      <Asd20MultiselectInput
        :value="selectedTagOptions"
        :items="tagOptions"
        label="Tags"
        required
        @input="editedFile.tags = $event.map(e => e.id)"
      />

      <Asd20TextInput
        type="date"
        v-model="lastModifiedDate"
        label="Date Modified"
        required
      />

      <Asd20FileInput
        v-if="!editedFile.id"
        type="file"
        label="Choose PDF, JPG, PNG, EPS or Excel files to upload"
        accept="application/pdf, image/jpg, image/png, application/postscript, application/x-pdf, application/vnd.ms-excel, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
        multiple
        @input="fileList = $event"
      />
      <asd20-list v-if="file && file.id">
        <asd20-list-item
          label="File Id"
          :description="`<small>${file.id}</small>`"
          condensed
        />
        <asd20-list-item
          label="Filename"
          :description="`<small>${file.filename}</small>`"
          condensed
        />
        <asd20-list-item
          label="Date/Time Uploaded"
          :description="new Date(file.createdDateTime).toLocaleString()"
          condensed
        />
        <asd20-list-item
          label="Updated/Uploaded By"
          :description="file.lastModifiedBy"
          condensed
        />
      </asd20-list>

      <asd20-list v-if="!editedFile.id">
        <asd20-list-item
          v-for="(selectedFile, index) in selectedFiles"
          :key="index"
          :label="selectedFile.name"
          :description="
            `<small>${selectedFile.humanFileSize}, ${
              selectedFile.status
            }</small>`
          "
          condensed
        />
      </asd20-list>

      <div class="button-group" v-if="editedFile.id">
        <Asd20Button
          reversed
          horizontal
          label="Save"
          @click.native="saveEditedFile"
        />
        <Asd20Button
          v-if="editedFile.id"
          transparent
          horizontal
          label="Delete"
          @click.native="deleteFile"
        />
      </div>

      <div class="button-group" v-if="!editedFile.id">
        <Asd20Button
          v-if="selectedFiles.length > 0"
          reversed
          horizontal
          label="Upload"
          @click.native="saveEditedFile"
        />
      </div>
    </Asd20Viewport>
  </asd20-modal>
</template>

<script>
import { get } from 'vuex-pathify'
import Asd20List from '@asd20/ui/src/components/organisms/Asd20List'
import Asd20ListItem from '@asd20/ui/src/components/molecules/Asd20ListItem'
import Asd20Modal from '@asd20/ui/src/components/molecules/Asd20Modal'
import Asd20Viewport from '@asd20/ui/src/components/atoms/Asd20Viewport'
import Asd20TextInput from '@asd20/ui/src/components/molecules/Asd20TextInput'
import Asd20FileInput from '@asd20/ui/src/components/molecules/Asd20FileInput'
import Asd20MultiselectInput from '@asd20/ui/src/components/atoms/Asd20MultiselectInput'
import { Promise } from 'q'
import { parse, format } from 'date-fns'

export default {
  name: 'Asd20FileModal',
  components: {
    Asd20Modal,
    Asd20Viewport,
    Asd20TextInput,
    Asd20FileInput,
    Asd20MultiselectInput,
    Asd20List,
    Asd20ListItem,
  },

  props: {
    file: { type: Object, default: null },
    open: { type: Boolean, default: false },
  },

  data: () => ({
    formValid: false,
    fileId: 0,
    editedFile: null,
    deleting: false,
    fileList: null,
    fileUploadStatuses: [],
    requiredRules: [
      v => (Array.isArray(v) ? v.length > 0 : !!v) || 'This field is required',
    ],
  }),

  computed: {
    lastModifiedDate: {
      get() {
        return format(parse(this.editedFile.lastModifiedDateTime), 'YYYY-MM-DD')
      },
      set(value) {
        const vdt = parse(value)
        let dt = parse(this.editedFile.lastModifiedDateTime)
        dt.setYear(vdt.getFullYear())
        dt.setMonth(vdt.getMonth())
        dt.setDate(vdt.getDate())
        this.editedFile.lastModifiedDateTime = dt.toISOString()
      },
    },

    categories: get('files/categories'),
    owners: get('files/owners'),
    tags: get('files/tags'),
    organizations: get('files/organizations'),

    selectedFiles() {
      if (
        typeof this.fileList === undefined ||
        !(this.fileList instanceof FileList)
      )
        return []
      let files = []

      for (const fileObject of this.fileList) {
        let status = this.fileUploadStatuses.find(
          s => s.filename === fileObject.name
        )
        files.push({
          lastModified: fileObject.lastModified,
          lastModifiedDate: fileObject.lastModifiedDate,
          name: fileObject.name,
          size: fileObject.size,
          humanFileSize: (size => {
            var i = Math.floor(Math.log(size) / Math.log(1024))
            return (
              (size / Math.pow(1024, i)).toFixed(2) * 1 +
              ' ' +
              ['B', 'kB', 'MB', 'GB', 'TB'][i]
            )
          })(fileObject.size),
          status: status
            ? `${status.progress * 100}% uploaded.`
            : 'ready to upload.',
          type: fileObject.type,
        })
      }
      return files
    },

    selectedCategories: get('files/selectedCategories'),
    selectedOwners: get('files/selectedOwners'),
    selectedOrganizationIds: get('files/selectedOrganizationIds'),

    categoryOptions() {
      return this.categories.map(c => ({
        id: c,
        name: c,
      }))
    },
    selectedCategoryOptions() {
      return (this.editedFile.categories || []).map(c => ({
        id: c,
        name: c,
      }))
    },
    selectedOrganizations() {
       return this.organizations.find(o => (this.editedFile.organizationIds || []).includes(o.id))
    },
    ownerOptions() {
      return this.owners.map(o => ({
        id: o,
        name: o,
      }))
    },
    selectedOwnerOptions() {
      return (this.editedFile.owners || []).map(c => ({
        id: c,
        name: c,
      }))
    },

    tagOptions() {
      return this.tags.map(o => ({
        id: o,
        name: o,
      }))
    },
    selectedTagOptions() {
      return (this.editedFile.tags || []).map(c => ({
        id: c,
        name: c,
      }))
    },
  },

  watch: {
    file: function() {
      this.updateEditedFile()
    },
  },

  methods: {
    updateEditedFile() {
      if (!this.file) return
      let file = JSON.parse(JSON.stringify(this.file))
      this.fileList = []
      if (!this.file.id) {
        file.categories = this.selectedCategories
        file.owners = this.selectedOwners
        file.organizations = this.selectedOrganizationOptions
      }
      this.editedFile = file

      if (!this.editedFile.lastModifiedDateTime) {
        this.editedFile.lastModifiedDateTime = new Date().toISOString()
      }
    },
    async selectFileFromCloud() {
      return new Promise((resolve, reject) => {
        window.OneDrive.open({
          clientId: '06559921-2186-44e9-8c8d-136a04dfc4e2',
          action: 'share',
          multiSelect: false,
          advanced: {
            // filter: '.pdf',
            navigation: {
              disable: true,
              entryLocation: {
                sharePoint: {
                  listPath:
                    'https://asd20.sharepoint.com/sites/DistrictWebsiteContent/Shared Documents',
                },
              },
              sourceTypes: ['Sites'],
            },
            createLinkParameters: {
              type: 'view',
              scope: 'anonymous',
            },
          },
          success: result => {
            console.log(result)
            const fileTypeMap = {
              'application/pdf': 'PDF',
              'image/jpg:': 'JPG',
              'image/png': 'PNG',
              'appication/postscript': 'EPS',
            }
            if (result.value.length > 0) {
              const selectedFile = result.value[0]
              this.editedFile.url = selectedFile.webUrl
              this.editedFile.filename = selectedFile.name
              this.editedFile.filesize = selectedFile.size
              this.editedFile.createdDateTime = selectedFile.createdDateTime
              this.editedFile.lastModifiedDateTime =
                selectedFile.lastModifiedDateTime
              this.editedFile.sourceId = selectedFile.id
              this.editedFile.downloadUrl =
                selectedFile['@microsoft.graph.downloadUrl']
              this.editedFile.mimeType = selectedFile.file.mimeType
              this.editedFile.filetype =
                fileTypeMap[selectedFile.file.mimeType] || ''

              if (!this.editedFile.id) {
                this.editedFile.name = selectedFile.name
                this.editedFile.slug = selectedFile.name
              }

              resolve(selectedFile)
            }
            resolve(null)
            /* success handler */
          },
          cancel: () => {
            /* cancel handler */
          },
          error: error => {
            console.log(error)
            reject(error)
            /* error handler */
          },
        })
      })
    },
    async saveEditedFile() {
      // If the file already exists
      if (this.file && this.file.id) {
        try {
          await this.$store.dispatch('files/saveFile', this.editedFile)
          this.$emit('dismiss')
        } catch (error) {
          // TODO: handle errors
          console.error(error)
        }
        // If we are uploading new files
      } else {
        if (this.selectedFiles.length > 0) {
          await this.$store.dispatch('files/uploadFile', {
            metadata: this.editedFile,
            fileList: this.fileList,
            uploadProgressCallback: status => {
              let statuses = this.fileUploadStatuses.filter(
                s => s.filename !== status.filename
              )
              statuses.push(status)
              this.fileUploadStatuses = statuses
            },
          })
          this.$emit('dismiss')
        }
      }
    },
    async deleteFile() {
      try {
        this.$store.dispatch('files/deleteFile', this.file)
        this.$emit('dismiss')
      } catch (error) {
        // TODO: handle errors
        console.error(error)
      }
    },
  },
}
</script>

<style lang="scss">
@import '@asd20/ui/src/design/_mixins.scss';
@import '@asd20/ui/src/design/_variables.scss';

.asd20-file-modal {
  .button-group {
    display: flex;
  }

  --accent-color: #{asd20-swatch('background-dark', 4)};

  &__header {
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-direction: column;
  }

  &__details {
    padding: space(1);
  }
}

// Keep these
@media (min-width: 1024px) {
  .asd20-file-modal {
    .asd20-modal {
      margin-top: auto;
      margin-bottom: auto;
      max-width: 40vw;
    }
    &__details {
      padding: space(1);
    }
  }
}
</style>
